/** @typedef {import('../../types/models').Transaction} Transaction */

import { useEffect, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { toast } from 'react-toastify';

import { fetchTransactions } from '../../api';
import { queryClient } from '../../config';
import { generatedDates } from '../../utils';

/**
 * @param {number} [currency] - Currency ID filter
 * @returns {{ recentTransactions: Transaction[], transactions: Transaction[], isFetching: boolean, isFetchingNextPage: boolean, fetchNextPage: Function, hasNextPage: boolean, searchQuery: string, setSearchQuery: Function, isRefetching: boolean, setStatus: Function, isLoading: boolean, status: string, filterByDate: Function }}
 */
const useFetchTransactions = (currency) => {
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  let recentTransactions = [];
  const {
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
    data,
    refetch,
    isRefetching,
    isLoading,
    error
  } = useInfiniteQuery(
    ['allTransactions'],
    ({ pageParam }) => {
      return fetchTransactions({ offset: pageParam, searchQuery, status, dateFilter, currency });
    },
    {
      getNextPageParam: (lastPage) => {
        const hasNext = lastPage.meta?.hasNext;
        const lastTransaction = lastPage && lastPage.data[lastPage.data.length - 1];
        return hasNext ? lastTransaction && lastTransaction.id : undefined;
      }
    }
  );
  if (isError) {
    toast.error(error, 'transactions');
  }
  useEffect(() => {
    if (data && data.pages) {
      let aggregatedTransactions = [];
      data.pages.map(({ data: transactionsList }) => {
        aggregatedTransactions = [...aggregatedTransactions, ...transactionsList];
      });
      setTransactions(aggregatedTransactions);
    }
  }, [data]);
  recentTransactions = transactions.slice(0, 4);

  useEffect(() => {
    reloadTransactions();
  }, [dateFilter, currency, status]);

  useEffect(() => {
    setStatus('');
    reloadTransactions();
  }, [searchQuery]);

  const filterByDate = (dateType) => {
    if (dateType) {
      setDateFilter({ from: generatedDates[dateType](), to: generatedDates.now() });
    } else {
      setDateFilter({ from: null, to: null });
    }
  };
  const reloadTransactions = () => {
    setTransactions([]);
    queryClient.removeQueries('allTransactions', { exact: true });
    refetch({ resetQueries: true });
  };

  return {
    recentTransactions,
    transactions,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    searchQuery,
    setSearchQuery,
    isRefetching,
    setStatus,
    isLoading,
    status,
    filterByDate
  };
};

export { useFetchTransactions };
